import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Autocomplete,
  Chip,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Search, UserPlus } from "lucide-react";
import Modal from "../common/CommonModal";
import CreditCustomerList from "../common/CreditCustomerList";
import useApi from "../../hooks/useApi";
import UnifiedCustomerForm from "../salesInvoice/CustomerForm";
import JobCodeList from "../common/JobCodeList";
import CashCustomerList from "../salesInvoice/CashCustomerList";
import checkIfUserHasAccess from "../common/UserCheckAccess";
const PurchaseGeneral = ({
  purchaseInvoiceData,
  setPurchaseInvoiceData,
  BASE_URL,
  COMPANY_CODE,
  DEPARTMENT_NO,
  setSupplierData,
  selectedSupplier,
  setSelectedSupplier,
  supplierData,
  mode, glSettings, setGlSettings
}) => {
  // console.log("selected Supplier", selectedSupplier)
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: "", // 'add', 'cr-list'
    customer: null,
  });
  const autocompleteRef = useRef();
  const [suggestions, setSuggestions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const { request, loading } = useApi();

  const areaCodes = [
    { ACODE_KEY: "DUBAI", acode_name: "Dubai" },
    { ACODE_KEY: "ABUDHABI", acode_name: "Abu Dhabi" },
    { ACODE_KEY: "SHARJAH", acode_name: "Sharjah" },
    { ACODE_KEY: "AJMAN", acode_name: "Ajman" },
    { ACODE_KEY: "UMMALQUWAIN", acode_name: "Umm Al Quwain" },
    { ACODE_KEY: "RAK", acode_name: "Ras Al Khaimah" },
    { ACODE_KEY: "FUJAIRAH", acode_name: "Fujairah" },
  ];

  const handleSupplierChange = (event, newValue) => {
    console.log("Supplier data new Value", newValue);
    setSelectedSupplier(newValue);
    setSupplierData(newValue);

    console.log("Selected Supplier", newValue);

    setPurchaseInvoiceData({
      ...purchaseInvoiceData,
      supplier: newValue,
      creditAcc:
        purchaseInvoiceData.paymentMode === "cash"
          ? ""
          : newValue?.ACCOUNT || "",
      trn: newValue?.TRN || "",
    });
  };

  const fetchCashSuppliers = async (query = "-") => {
    const safeQuery = encodeURIComponent(query);
    const url = `${BASE_URL}/api/CRMAccountList/${COMPANY_CODE}/WALKINLISTSUPP/${DEPARTMENT_NO}/supplier/-/${safeQuery}`;
    try {
      const response = await request({
        url: url,
        method: "GET",
        timeout: 1000,
      });
      return response || [];
    } catch (err) {
      console.error("Failed to fetch cash suppliers", err);
      return [];
    }
  };

  useEffect(() => {
    if (
      (COMPANY_CODE?.toUpperCase() === "SUNRISEJP" ||
        COMPANY_CODE?.toUpperCase() === "SUNRISEWEB") &&
      mode === "new" &&
      !purchaseInvoiceData.jobCode
    ) {
      setPurchaseInvoiceData((prev) => ({
        ...prev,
        jobCode: "GIT",
      }));
    }
  }, [COMPANY_CODE, mode, purchaseInvoiceData.jobCode, setPurchaseInvoiceData]);

  const fetchCreditSuppliers = async (query = "-") => {
    console.log("fetchCreditSuppliers Query", query);
    const safeQuery = encodeURIComponent(query);
    const url = `${BASE_URL}/api/CRMAccountList/${COMPANY_CODE}/ACCOUNTLIST/${DEPARTMENT_NO}/all/-/${safeQuery}`;
    try {
      const response = await request({
        url: url,
        method: "GET",
        timeout: 1000,
      });
      return response || [];
    } catch (err) {
      console.error("Failed to fetch credit suppliers", err);
      return [];
    }
  };

  const fetchSuggestions = useCallback(
    async (query) => {
      if (!query || query.trim().length === 0) {
        setSuggestions([]);
        return;
      }

      const freetext = query.trim();

      if (purchaseInvoiceData.paymentMode === "cash") {
        const result = await fetchCashSuppliers(freetext);
        setSuggestions(result || []);
      } else if (purchaseInvoiceData.paymentMode === "credit") {
        const result = await fetchCreditSuppliers(freetext);
        setSuggestions(result || []);
      } else {
        setSuggestions([]);
      }
    },
    [purchaseInvoiceData.paymentMode, BASE_URL, COMPANY_CODE, DEPARTMENT_NO],
  );

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      fetchSuggestions(inputValue);
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [inputValue, fetchSuggestions]);

  const handleAddSupplier = () => {
    setModalState({
      isOpen: true,
      type: "add",
      customer: null,
    });
  };

  const getAccountValues = (gls, { purchaseType, invmethod }) => {
    const findAcc = (code) =>
      gls.find((x) => x.Scrn_code === code)?.ACCOUNT?.trim() || "";

    const findAccName = (code) =>
      gls.find((x) => x.Scrn_code === code)?.accdesc?.trim() || "";

    let vatACC = "########";
    let vatAccName = "";
    let txtDr = "########";
    let txtDrName = "";
    let vatimpcr = "########";
    let vatimpcrName = "";
    let cashcus = "########";
    let cashcusName = "";

    const isLocal = purchaseType === "Local";
    const isImport = purchaseType === "Import";

    // VAT Account Logic (matches backend)
    if (isLocal) {
      vatACC = findAcc("IR-VT-D") || "########";
      vatAccName = findAccName("IR-VT-D") || "";
    } else {
      vatACC = findAcc("IR-VTR-D") || "########";
      vatAccName = findAccName("IR-VTR-D") || "";
      // If IR-VTR-D is empty or not 8 characters, fallback to IR-VT-D
      if (!vatACC || vatACC.length !== 8) {
        vatACC = findAcc("IR-VT-D") || "########";
        vatAccName = findAccName("IR-VT-D") || "";
      }
    }

    // Import VAT Credit Account
    if (isImport) {
      vatimpcr = findAcc("IR-VT-C") || "########";
      vatimpcrName = findAccName("IR-VT-C") || "";
    }

    // Debit Account Logic
    if (isImport) {
      txtDr = findAcc("IR-IM-D") || "########";
      txtDrName = findAccName("IR-IM-D") || "";
    } else if (isLocal) {
      txtDr = findAcc("IR-LO-D") || "########";
      txtDrName = findAccName("IR-LO-D") || "";
    }

    // Cash Customer Account
    cashcus = findAcc("IR-CA-C") || "########";
    cashcusName = findAccName("IR-CA-C") || "";

    // Override for COS method
    if (invmethod === "COS") {
      txtDr = findAcc("IR-STK-D") || "########";
      txtDrName = findAccName("IR-STK-D") || "";
    }

    return {
      vatACC,
      debitAcc: txtDr,
      vatimpcr,
      cashcus,
      vatAccName,
      txtDrName,
      vatimpcrName,
      cashcusName,
    };
  };



  const fetchNextNumber = useCallback(async () => {
    const url = `${BASE_URL}/api/CRMDocListView/${COMPANY_CODE}/GRVSERIAL/I/-/-/-/-/${DEPARTMENT_NO}/0/0`;

    try {
      const response = await request({ url, method: "GET" });

      if (response?.length > 0) {
        const serial = response[0].GRV_Serial;
        // Example: LP-2315-220426

        const parts = serial.split("-");

        if (parts.length >= 2) {
          const sequencePart = parts[1];
          // 2315

          setPurchaseInvoiceData((prev) => ({
            ...prev,
            prefix: sequencePart,
          }));
        }
      }
    } catch (error) {
      console.error("Failed to fetch NextNumber", error);
    }
  }, [BASE_URL, COMPANY_CODE, DEPARTMENT_NO]);



  useEffect(() => {
    if (!glSettings.length) return;

    const accounts = getAccountValues(glSettings, {
      purchaseType: purchaseInvoiceData.purchaseType,
      invmethod: purchaseInvoiceData.invmethod,
    });

    setPurchaseInvoiceData((prev) => {
      let newCreditAcc = "######";

      if (prev.paymentMode === "cash") {
        // CASE 1: Cash Mode -> Always use GL Cash Account
        newCreditAcc = accounts.cashcus;
      } else {
        // CASE 2: Credit Mode

        // Priority 1: Use explicitly selected supplier
        if (selectedSupplier?.ACCOUNT) {
          newCreditAcc = selectedSupplier.ACCOUNT;
        }
        // Priority 2: Use supplier object in state
        else if (prev.supplier?.ACCOUNT) {
          newCreditAcc = prev.supplier.ACCOUNT;
        }
        // Priority 3: Intelligent Fallback
        else {
          // If the current creditAcc is SAME as the Cash Account, it means
          // we just switched from Cash -> Credit. Reset it to ######.
          if (prev.creditAcc === accounts.cashcus) {
            newCreditAcc = "######";
          }
          // If we have a valid account that IS NOT the cash account
          // (e.g., loaded from DB in Edit mode), Keep it.
          else if (
            prev.creditAcc &&
            prev.creditAcc !== "######" &&
            prev.creditAcc !== ""
          ) {
            newCreditAcc = prev.creditAcc;
          }
        }
      }

      return {
        ...prev,
        debitAcc: accounts.debitAcc,
        vatAcc: accounts.vatACC,
        vatAccImp: accounts.vatimpcr,
        creditAcc: newCreditAcc,

        vatAccName: accounts.vatAccName,
        debitAccName: accounts.txtDrName,
        vatAccImpName: accounts.vatimpcrName,
        creditAccName: accounts.cashcusName,
      };
    });
  }, [
    glSettings,
    purchaseInvoiceData.purchaseType,
    purchaseInvoiceData.invmethod,
    purchaseInvoiceData.paymentMode,
    selectedSupplier,
    setPurchaseInvoiceData,
  ]);

  useEffect(() => {
    if (mode !== "edit" || !selectedSupplier?.ACCOUNT) return;

    const fetchFullSupplierDetails = async () => {
      try {
        const query = encodeURIComponent(selectedSupplier.ACCOUNT);
        const url = `${BASE_URL}/api/CRMAccountList/${COMPANY_CODE}/ACCOUNTLIST/${DEPARTMENT_NO}/all/-/${query}`;
        const response = await request({ url, method: "GET", timeout: 5000 });
        if (response?.length > 0) {
          const match = response.find(
            (s) => s.ACCOUNT === selectedSupplier.ACCOUNT,
          );
          if (match) {
            setSelectedSupplier(match);
            setSupplierData(match);
            setInputValue(`${match.ACCOUNT} ${match.DESCRIPTION || ""}`);
            setPurchaseInvoiceData((prev) => ({
              ...prev,
              supplier: match,
              trn: match?.TRN || "",
              creditAcc:
                prev.paymentMode === "cash"
                  ? ""
                  : match?.ACCOUNT || "",
            }));
          }
        }
      } catch (err) {
        console.error("Failed to fetch full supplier details", err);
      }
    };

    fetchFullSupplierDetails();
  }, [mode, selectedSupplier?.ACCOUNT]);

  const handleSaveSupplier = async (supplierData) => {
    console.log("OnSave", supplierData);
    try {
      const url = `${BASE_URL}/api/CRMAcmastCRM`;
      const response = await request({
        url: url,
        method: "POST",
        data: supplierData,
        config: {
          headers: {
            "Content-Type": "application/json",
          },
        },
        timeout: 5000,
      });
      console.log("Supplier saved successfully:", response);
      alert("Supplier saved successfully");
      await fetchSuggestions(inputValue);
      closeModal();
    } catch (error) {
      console.error(
        "Upload Error:",
        error.response ? error.response.data : error.message,
      );
      alert(
        "Failed to save supplier: " +
        (error.response ? error.response.data : error.message),
      );
    }
  };
  const closeModal = () => {
    setModalState({
      isOpen: false,
      type: "",
      customer: null,
    });
  };

  const getOptionLabel = (option) => {
    if (!option) return "";
    return option.DESCRIPTION || option.name || "";
  };
  const generateImportDocRef = (date, sequence) => {
    const dateStr = date || new Date().toISOString().split("T")[0];

    try {
      const [year, month, day] = dateStr.split("-");

      const formattedDate = `${day}${month}${year.slice(-2)}`;

      return `IP-${sequence}-${formattedDate}`;
    } catch {
      return `IP-${sequence}-XXXXXX`;
    }
  };
  useEffect(() => {
    if (
      (COMPANY_CODE?.toUpperCase() === "SUNRISEJP" ||
        COMPANY_CODE?.toUpperCase() === "SUNRISEWEB") &&
      purchaseInvoiceData.purchaseType === "Import" &&
      mode !== "view" &&
      mode !== "edit"
    ) {
      fetchNextNumber();
    }
  }, [purchaseInvoiceData.purchaseType, COMPANY_CODE, mode, fetchNextNumber]);

  useEffect(() => {
    if (purchaseInvoiceData.prefix) {
      const newDocRef = generateImportDocRef(
        purchaseInvoiceData.date,
        purchaseInvoiceData.prefix,
      );

      setPurchaseInvoiceData((prev) => ({
        ...prev,
        docRef: newDocRef,
      }));
    }
  }, [purchaseInvoiceData.date, purchaseInvoiceData.prefix]);

  // Auto-set VAT Status based on Purchase Type
  useEffect(() => {
    if (purchaseInvoiceData.purchaseType === "Local") {
      setPurchaseInvoiceData((prev) => ({
        ...prev,
        vatStatus: "VAT Applied",
      }));
    }
  }, [purchaseInvoiceData.purchaseType, setPurchaseInvoiceData]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-4">
      <div className="lg:col-span-4 col-span-1 flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <FormControl
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexShrink: 0,
            }}
          >
            {(() => {
              const hasAG = checkIfUserHasAccess("-*AG");
              const hasAH = checkIfUserHasAccess("-*AH");
              const showLocal = !hasAH || hasAG;
              const showImport = !hasAG || hasAH;
              return (
                <RadioGroup
                  row
                  name="purchaseType"
                  value={purchaseInvoiceData.purchaseType || "Local"}
                  onChange={(e) =>
                    setPurchaseInvoiceData({
                      ...purchaseInvoiceData,
                      purchaseType: e.target.value,
                    })
                  }
                >
                  {showLocal && (
                    <FormControlLabel
                      value="Local"
                      control={<Radio size="small" />}
                      label="Local"
                    />
                  )}
                  {showImport && (
                    <FormControlLabel
                      value="Import"
                      control={<Radio size="small" />}
                      label="Import"
                    />
                  )}
                </RadioGroup>
              );
            })()}
          </FormControl>

          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-sm text-gray-600">SRV No:</span>
            <span className="text-red-500 font-semibold">
              {purchaseInvoiceData.documentNo}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TextField
            select
            label="VAT Status"
            variant="outlined"
            size="small"
            value={purchaseInvoiceData.vatStatus || "VAT Applied"}
            onChange={(e) =>
              setPurchaseInvoiceData({
                ...purchaseInvoiceData,
                vatStatus: e.target.value,
              })
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
          >
            <MenuItem value="VAT Applied">VAT Applied</MenuItem>
            <MenuItem value="0% VAT">0% VAT</MenuItem>
            <MenuItem value="VAT Exempted">VAT Exempted</MenuItem>
            <MenuItem value="Out of Scope">Out of Scope</MenuItem>
          </TextField>

          <TextField
            label="Purchase Date"
            type="date"
            variant="outlined"
            size="small"
            value={
              purchaseInvoiceData.date || new Date().toISOString().split("T")[0]
            }
            onChange={(e) => {
              const newDate = e.target.value;
              setPurchaseInvoiceData({
                ...purchaseInvoiceData,
                date: newDate,
              });
            }}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </div>

        <div className="p-3 bg-gray-100 flex justify-between rounded-lg">
          <div>
            <span className="text-sm text-gray-600">Credit A/C:</span>{" "}
            <span className="font-bold text-orange-600">
              {purchaseInvoiceData.creditAcc || "######"}
            </span>
          </div>
          <div>
            <span className="text-sm text-gray-600">Debit A/C:</span>
            <span className="font-bold text-green-600">
              {purchaseInvoiceData.debitAcc || "#####"}
            </span>
          </div>
        </div>
      </div>

      <div className="lg:col-span-4 col-span-1 flex flex-col gap-4">
        <FormControl sx={{ display: "flex", gap: 2 }}>
          <RadioGroup
            row
            name="paymentMode"
            value={purchaseInvoiceData.paymentMode || "cash"}
            onChange={(e) => {
              const newMode = e.target.value;

              // 1. Reset the UI Selection States immediately
              setSelectedSupplier(null);
              setInputValue("");
              setSuggestions([]);

              // 2. Reset the Prop Data (if passed)
              if (setSupplierData) {
                setSupplierData(null);
              }

              // 3. Update the Main Form State
              setPurchaseInvoiceData((prev) => ({
                ...prev,
                paymentMode: newMode,
                supplier: null, // Clear the stored supplier object
              }));
            }}
          >
            <FormControlLabel
              value="cash"
              control={<Radio size="small" />}
              label="Cash"
            />
            <FormControlLabel
              value="credit"
              control={<Radio size="small" />}
              label="Credit"
            />
            <FormControlLabel
              value="ccard"
              control={<Radio size="small" />}
              label="C-Card"
            />
          </RadioGroup>
        </FormControl>

        <Autocomplete
          ref={autocompleteRef}
          value={selectedSupplier}
          onChange={handleSupplierChange}
          inputValue={inputValue}
          autoFocus
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          options={suggestions}
          getOptionLabel={getOptionLabel}
          loading={loading}
          filterOptions={(x) => x} // Disable built-in filtering
          renderInput={(params) => (
            <TextField
              {...params}
              label="Supplier"
              variant="outlined"
              size="small"
              autoFocus
              placeholder="Search supplier..."
              onKeyDown={(e) => {
                if (e.key === "F2") {
                  e.preventDefault();

                  // CLOSE AUTOCOMPLETE SUGGESTIONS
                  if (autocompleteRef.current) {
                    autocompleteRef.current.blur();
                  }

                  // OPEN MODAL
                  if (purchaseInvoiceData.paymentMode === "credit") {
                    setModalState({
                      isOpen: true,
                      type: "cr-list",
                      customer: null,
                    });
                  } else if (purchaseInvoiceData.paymentMode === "cash") {
                    setModalState({
                      isOpen: true,
                      type: "cash-supplier-list",
                      customer: null,
                    });
                  }
                }
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                    {purchaseInvoiceData.paymentMode === "cash" && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleAddSupplier}
                          edge="end"
                          size="small"
                          color="primary"
                          sx={{ ml: 0.5, p: 0.5, borderRadius: "8px" }}
                        >
                          <UserPlus size={18} />
                        </IconButton>
                      </InputAdornment>
                    )}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <div className="flex items-center justify-between w-full">
                <span>{option.DESCRIPTION || option.name}</span>
                {option.ACCOUNT && (
                  <Chip
                    label={option.ACCOUNT}
                    size="small"
                    color={"primary"}
                    sx={{ height: 20, fontSize: 10 }}
                  />
                )}
              </div>
            </li>
          )}
          noOptionsText={
            inputValue.length === 0
              ? "Start typing to search..."
              : "No suppliers found"
          }
        />

        <div className="flex gap-4">
          <TextField
            label="Due Days"
            variant="outlined"
            size="small"
            value={purchaseInvoiceData.dueDays || ""}
            onChange={(e) =>
              setPurchaseInvoiceData({
                ...purchaseInvoiceData,
                dueDays: e.target.value,
              })
            }
            fullWidth
          />
          <TextField
            label="Due Date"
            type="date"
            variant="outlined"
            size="small"
            value={
              purchaseInvoiceData.dueDate ||
              new Date().toISOString().split("T")[0]
            }
            onChange={(e) =>
              setPurchaseInvoiceData({
                ...purchaseInvoiceData,
                dueDate: e.target.value,
              })
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </div>
      </div>

      <div className="lg:col-span-4 col-span-1">
        <div className="flex flex-col gap-4">
          {/* Row 1: Internal Reference (Conditional) */}
          {(COMPANY_CODE?.toUpperCase() === "SUNRISEJP" ||
            COMPANY_CODE?.toUpperCase() === "SUNRISEWEB") &&
            purchaseInvoiceData.purchaseType === "Import" ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col justify-center px-3 py-2 bg-slate-50 border border-dashed border-slate-200 rounded-lg">
                <div className="flex items-baseline gap-1 font-mono font-bold text-sm">
                  <span className="text-blue-900">
                    {purchaseInvoiceData.docRef}
                  </span>
                </div>

                <span className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">
                  IP + sequence + (DDMMYY)
                </span>
              </div>
            </div>
          ) : null}

          {/* Row 2: Standard Info */}
          <div className="grid grid-cols-2 gap-4">
            <TextField
              label="Supp. Inv No"
              fullWidth
              size="small"
              variant="outlined"
              value={purchaseInvoiceData.invNumber || ""}
              onChange={(e) =>
                setPurchaseInvoiceData({
                  ...purchaseInvoiceData,
                  invNumber: e.target.value,
                })
              }
            />
            <TextField
              label="TRN"
              fullWidth
              size="small"
              variant="outlined"
              value={purchaseInvoiceData.trn || ""}
              required
              inputProps={{ maxLength: 15 }}
              error={
                purchaseInvoiceData.trn && purchaseInvoiceData.trn.length !== 15
              }
              helperText={
                purchaseInvoiceData.trn && purchaseInvoiceData.trn.length !== 15
                  ? "TRN must be 15 digits"
                  : ""
              }
              onChange={(e) =>
                setPurchaseInvoiceData({
                  ...purchaseInvoiceData,
                  trn: e.target.value,
                })
              }
            />
          </div>

          {/* Row 3: Additional Details */}
          <div className="grid grid-cols-2 gap-4">
            {COMPANY_CODE?.toUpperCase() === "SUNRISEJP" ||
              COMPANY_CODE?.toUpperCase() === "SUNRISEWEB" ? (
              <TextField
                select
                label="Status"
                fullWidth
                size="small"
                variant="outlined"
                value={purchaseInvoiceData.jobCode || "GIT"}
                onChange={(e) =>
                  setPurchaseInvoiceData({
                    ...purchaseInvoiceData,
                    jobCode: e.target.value,
                  })
                }
              >
                <MenuItem value="GIT">GIT</MenuItem>
                <MenuItem value="CONFIRMED">CONFIRMED</MenuItem>
              </TextField>
            ) : (
              <TextField
                label="Unreg Supplier"
                fullWidth
                size="small"
                variant="outlined"
                value={purchaseInvoiceData.unregSupplier || ""}
                onChange={(e) =>
                  setPurchaseInvoiceData({
                    ...purchaseInvoiceData,
                    unregSupplier: e.target.value,
                  })
                }
              />
            )}

            {COMPANY_CODE?.toUpperCase() !== "SUNRISEJP" &&
              COMPANY_CODE?.toUpperCase() !== "SUNRISEWEB" && (
                <TextField
                  label="Job Code"
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={purchaseInvoiceData.jobCode || ""}
                  onKeyDown={(e) => {
                    if (e.key === "F2") {
                      e.preventDefault();
                      setModalState({ isOpen: true, type: "job-list" });
                    }
                  }}
                  onChange={(e) =>
                    setPurchaseInvoiceData({
                      ...purchaseInvoiceData,
                      jobCode: e.target.value,
                    })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setModalState({ isOpen: true, type: "job-list" })
                          }
                          edge="end"
                        >
                          <Search size={16} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={
          modalState.type === "add"
            ? "Add New Supplier"
            : modalState.type === "cr-list"
              ? "Select Supplier"
              : modalState.type === "job-list"
                ? "Select Job Code"
                : "Supplier"
        }
        size={modalState.type === "add" ? "xxl" : "xxl"}
        header={modalState.type === "add" ? false : true}
      >
        {modalState.type === "add" && (
          <UnifiedCustomerForm
            mode="register"
            onSave={handleSaveSupplier}
            onCancel={closeModal}
            areaCodes={areaCodes}
            customer={{ custtype: "s" }}
          />
        )}
        {modalState.type === "cr-list" && (
          <CreditCustomerList
            defaultType="Suppliers"
            handleCustomerSelect={(newValue) => {
              console.log("Supplier data new Value", newValue);
              setSelectedSupplier(newValue);
              setSupplierData(newValue);
              console.log("Selected Supplier", newValue);
              setPurchaseInvoiceData({
                ...purchaseInvoiceData,
                supplier: newValue,
                creditAcc:
                  purchaseInvoiceData.paymentMode === "cash"
                    ? ""
                    : newValue?.ACCOUNT || "",
                trn: newValue?.TRN || "",
              });
              closeModal();
            }}
          />
        )}{" "}
        {modalState.type === "cash-supplier-list" && (
          <CashCustomerList
            type="supplier"
            handleCustomerSelect={(selectedItem) => {
              handleSupplierChange(null, selectedItem);
              setModalState({ isOpen: false, type: "", customer: null });
            }}
          />
        )}
        {modalState.type === "job-list" && (
          <JobCodeList
            open={modalState.isOpen}
            onClose={closeModal}
            BASE_URL={BASE_URL}
            COMPANY_CODE={COMPANY_CODE}
            onSelect={(job) => {
              setPurchaseInvoiceData({
                ...purchaseInvoiceData,
                jobCode: job.JobCode,
              });
              closeModal();
            }}
          />
        )}
      </Modal>
    </div>
  );
};

export default PurchaseGeneral;
